import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Loader2 } from 'lucide-react';
import { api } from './api';
import { useSafeBack } from '@/utils/useSafeBack';

const STAGES = [
  { id: 'trying',     label: 'Trying to conceive' },
  { id: 'pregnant',   label: "I'm pregnant (set my LMP)" },
  { id: 'postpartum', label: "I've had my baby (postpartum)" },
];

const MaternalSetup = ({ onSaved }) => {
  const navigate = useNavigate();
  // MaternalSetup is rendered inline by MaternalHub on /maternal-health
  // when no profile exists. Falling back to /maternal-health would loop
  // the user back to this same screen, so the safe fallback is the
  // parent Family Health surface. Natural history pop still works.
  const handleBack = useSafeBack('/family-health');
  const [mode, setMode] = useState('');
  const [lmp, setLmp] = useState('');
  const [ppStart, setPpStart] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const today = new Date().toISOString().slice(0, 10);
  const oneYearAgo = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const canSave =
    (mode === 'trying') ||
    (mode === 'pregnant' && /^\d{4}-\d{2}-\d{2}$/.test(lmp)) ||
    (mode === 'postpartum' && /^\d{4}-\d{2}-\d{2}$/.test(ppStart));

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const payload = mode === 'trying'
        ? { stage_override: 'trying' }
        : mode === 'pregnant'
          ? { lmp_date: lmp }
          : { postpartum_start_date: ppStart };
      await api.saveProfile(payload);
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white pb-24" data-testid="maternal-setup">
      <div className="max-w-md mx-auto px-4 sm:px-6 py-6">
        <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
            <Heart className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Set your stage</h1>
            <p className="text-xs text-gray-500 mt-0.5">We adapt content, alerts, and check-ins to where you are.</p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          {STAGES.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setMode(s.id)}
              className={`w-full text-left text-sm font-medium rounded-xl border-2 px-4 py-3 transition-all ${
                mode === s.id ? 'border-rose-500 bg-rose-50 text-rose-900' : 'border-gray-200 hover:border-rose-300 text-gray-700'
              }`}
              data-testid={`setup-stage-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {mode === 'pregnant' && (
          <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">First day of your last menstrual period (LMP)</label>
            <input
              type="date"
              value={lmp}
              max={today}
              min={oneYearAgo}
              onChange={(e) => setLmp(e.target.value)}
              className="w-full text-base font-medium border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-rose-400"
              data-testid="setup-lmp"
            />
            <p className="text-[11px] text-gray-500 mt-1.5">We'll calculate weeks and trimester automatically.</p>
          </div>
        )}

        {mode === 'postpartum' && (
          <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Date of birth (your baby's)</label>
            <input
              type="date"
              value={ppStart}
              max={today}
              min={oneYearAgo}
              onChange={(e) => setPpStart(e.target.value)}
              className="w-full text-base font-medium border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-rose-400"
              data-testid="setup-ppstart"
            />
            <p className="text-[11px] text-gray-500 mt-1.5">Your recovery support will adapt to your week postpartum.</p>
          </div>
        )}

        {error && <p className="text-sm text-rose-700 mb-2">{error}</p>}

        <button
          onClick={save}
          disabled={!canSave || saving}
          className={`w-full inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold shadow-sm transition-all ${
            canSave && !saving ? 'bg-gradient-to-br from-rose-500 to-pink-500 hover:brightness-110 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
          data-testid="setup-save"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {saving ? 'Saving…' : 'Save & continue'}
        </button>
      </div>
    </div>
  );
};

export default MaternalSetup;
